import { DERIVED_RIDE_STATUSES } from "score-common";

/**
 * #2852 — THE single named predicate for "this class needs placings", used
 * by every surface that renders the tag so they can never disagree again:
 *
 * - useClassesNeedingPlacings (ClassManager row badge + header count chip,
 *   the Placings nav badge in App.jsx) — feed it each ride's judged status
 *   (batchRideStatus' derived status) as `status`;
 * - ClassPlacings' deriveClassSummary (picker chip + "Needs placings only"
 *   filter) — getClassResults rows already carry that same judged-status
 *   domain in their own `status` field.
 *
 * A class needs placings exactly when the operator can commit placings for
 * it AND placing hasn't started yet:
 *
 * 1. it has at least one non-SCRATCHED ride;
 * 2. EVERY non-SCRATCHED ride's judged status is FINAL — the same bar
 *    score-api's setClassPlacings / PUT /ride/:rideId/placing enforce
 *    (#2113, isPlaceableStatus): CONFIRMED is NOT enough anymore, an
 *    unsigned sheet can still be reopened, and a tag that invited placing
 *    a CONFIRMED class walked the operator into a 409. FINAL also fires on
 *    the deliberate terminal acts (admin force-final, active ride-level
 *    No Show/Excused), which is exactly right — those rides are placeable;
 * 3. NO non-SCRATCHED ride has a placing committed yet — "zero placed",
 *    NOT "at least one unplaced" (#2852 driver decision, superseding
 *    #1079's "some placed, one blank still badges" rule: a fully-FINAL
 *    class with one eliminated/no-show/excused ride reaches FINAL but that
 *    ride is never placed, #1079's rule badged it forever even once every
 *    placeable ride was placed; the tag now clears the moment the operator
 *    has committed any placing for the class, since placing is a
 *    whole-class action). placing 0 is a legitimate placing (#534), so
 *    only null/undefined counts as "not yet placed".
 *
 * SCRATCHED rides are excluded from ALL THREE checks: they never need
 * judging, so they never block all-FINAL (score-api's own placing gate
 * skips them too), and they never report a placing (#2840 stamps their
 * result placing null), so they must not masquerade as "already placed"
 * (or as "unplaced") on either side of the check.
 *
 * Ride state only — never the clock (#340): a class whose scheduled time
 * has passed but whose rides aren't FINAL isn't waiting on placings, it's
 * still being scored, so it must NOT qualify.
 *
 * @param {{status?: string, placing?: number|null}[]|null|undefined} rides the class's rides, each carrying its judged status (DERIVED_RIDE_STATUSES domain) and placing
 * @returns {boolean} true exactly when the "Needs placings" tag should render
 */
export function classNeedsPlacings(rides) {
  if (!rides || rides.length === 0) return false;
  const counted = rides.filter((r) => r.status !== DERIVED_RIDE_STATUSES.SCRATCHED);
  if (counted.length === 0) return false;
  const allFinal = counted.every((r) => r.status === DERIVED_RIDE_STATUSES.FINAL);
  if (!allFinal) return false;
  return counted.every((r) => r.placing == null);
}

export default classNeedsPlacings;
